import { useEffect, useRef, type RefObject } from "react";

const MIN_LINES = 2;
const MAX_LINES = 8;

/** True while Mac dictation, Whisper, or an IME is still committing text. */
export function isDictationKey(event: {
    key: string;
    nativeEvent: { isComposing: boolean; keyCode: number };
}): boolean {
    return (
        event.nativeEvent.isComposing ||
        event.key === "Process" ||
        event.nativeEvent.keyCode === 229
    );
}

/**
 * Content height of a textarea without setting the live field to `height: auto`.
 * That reset drops the insertion point, so Mac Whisper and dictation stop typing.
 */
export function measureTextareaHeight(el: HTMLTextAreaElement): number {
    const style = getComputedStyle(el);
    const probe = document.createElement("textarea");
    probe.value = el.value;
    probe.rows = 1;
    probe.setAttribute("aria-hidden", "true");
    probe.tabIndex = -1;
    probe.style.position = "fixed";
    probe.style.top = "0";
    probe.style.left = "-9999px";
    probe.style.visibility = "hidden";
    probe.style.pointerEvents = "none";
    probe.style.height = "0";
    probe.style.minHeight = "0";
    probe.style.maxHeight = "none";
    probe.style.width = `${el.clientWidth}px`;
    probe.style.boxSizing = style.boxSizing;
    probe.style.padding = style.padding;
    probe.style.border = style.border;
    probe.style.font = style.font;
    probe.style.lineHeight = style.lineHeight;
    probe.style.letterSpacing = style.letterSpacing;
    probe.style.whiteSpace = "pre-wrap";
    probe.style.wordBreak = style.wordBreak;
    probe.style.overflowWrap = style.overflowWrap;
    probe.style.overflow = "hidden";
    document.body.appendChild(probe);
    const height = probe.scrollHeight;
    probe.remove();
    return height;
}

/**
 * Grows a composer textarea with its text, up to MAX_LINES, then lets the
 * textarea itself scroll. The live field's height is only written when the
 * line count changes, and never while dictation is in progress.
 */
export function useAutoGrowTextarea(
    ref: RefObject<HTMLTextAreaElement | null>,
    value: string
) {
    const composing = useRef(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const apply = () => {
            if (composing.current) return;
            const styles = getComputedStyle(el);
            const line = parseFloat(styles.lineHeight) || 20;
            const padding =
                parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
            const content = Math.max(
                measureTextareaHeight(el),
                line * MIN_LINES + padding
            );
            const next = Math.min(content, line * MAX_LINES + padding);
            if (el.style.height !== `${next}px`) {
                el.style.height = `${next}px`;
            }
            if (el.selectionStart === el.value.length) {
                el.scrollTop = el.scrollHeight;
            }
        };

        const onStart = () => {
            composing.current = true;
        };
        const onEnd = () => {
            composing.current = false;
            apply();
        };

        el.addEventListener("compositionstart", onStart);
        el.addEventListener("compositionend", onEnd);
        apply();
        return () => {
            el.removeEventListener("compositionstart", onStart);
            el.removeEventListener("compositionend", onEnd);
        };
    }, [ref, value]);
}
